import { useAuth } from '@clerk/clerk-react';
import type {
  CreateApiClientBody,
  CreateUser,
  CreateWorkspace,
  AdminCreateCountryBody,
  UpdateApiClientBody,
  AdminCreateInstitutionBody,
  AdminCreateReferenceBody,
  AdminCreateSourceBody,
  AdminCreateUpdateBody,
  AdminCreateVersionBody,
  AdminListReferencesQuery,
  AdminListUpdatesQuery,
  AdminUpdateCountryBody,
  AdminUpdateInstitutionBody,
  AdminUpdateReferenceBody,
  AdminUpdateSourceBody,
  AdminUpdateUpdateBody,
  AdminUpdateVersionBody,
  AdminUpdateWorkspaceBody,
  UpdateAdminReferenceDetailBody,
  StartSourceScrapingBody,
  ConfirmSourceScrapingBody,
  AdminCreateConsultantOrgBody,
  AdminUpdateConsultantOrgBody,
} from '@ditto/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuthenticatedClient, useProtectedQuery } from './useApi';

export const useBackOfficeUsersApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['back-office-users'],
    queryFn: async () => {
      const response = await client.backOffice.getAllUsers();

      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch users');
    },
    retry: 1,
  });
};

export const useBackOfficeWorkspacesApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['back-office-workspaces'],
    queryFn: async () => {
      const response = await client.backOffice.getAllWorkspaces();

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    retry: 1,
  });
};

export const useUpdateUserTypeApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      type,
    }: {
      userId: string;
      type: 'ditto' | 'client' | 'consultant';
    }) => {
      const response = await client.backOffice.updateUserType({
        body: { id: userId, type },
      });

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-users'] });
    },
  });
};

export const useCreateUserApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: CreateUser) => {
      const response = await client.backOffice.createUser({
        body: userData,
      });

      if (response.status === 201) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      // Attach the full response for additional debugging
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-users'] });
    },
  });
};

export const useCreateWorkspaceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workspaceData: CreateWorkspace) => {
      const response = await client.workspaces.createWorkspace({
        body: workspaceData,
      });

      if (response.status === 201) {
        return response.body;
      }
      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-workspaces'] });
    },
  });
};

export const useUpdateUserWorkspacesApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userId: string; workspaceIds: string[] }) => {
      const response = await client.backOffice.updateUserWorkspaces({
        body: data,
      });

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-users'] });
    },
  });
};

export const useDeleteWorkspaceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const response = await client.backOffice.deleteWorkspace({
        params: { id: workspaceId },
        body: undefined as any,
      });

      if (response.status === 200) {
        return response.body;
      }

      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-workspaces'] });
    },
  });
};

export const useBulkDeleteWorkspacesApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await client.backOffice.bulkDeleteWorkspaces({
        body: { ids },
      });

      if (response.status === 200) {
        return response.body;
      }

      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-workspaces'] });
    },
  });
};

export const useBulkDeleteUsersApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await client.backOffice.bulkDeleteUsers({
        body: { ids },
      });

      if (response.status === 200) {
        return response.body;
      }

      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-users'] });
    },
  });
};

export const useUpdateWorkspaceMembersApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { workspaceId: string; memberIds: string[] }) => {
      const response = await client.workspaces.updateWorkspaceMembers({
        body: data,
      });

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['back-office-users'] });
    },
  });
};

// ── Admin Update Workspace (unified) ──

export const useAdminUpdateWorkspaceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      ...body
    }: AdminUpdateWorkspaceBody & { workspaceId: string }) => {
      const response = await client.backOffice.updateWorkspace({
        params: { id: workspaceId },
        body,
      });

      if (response.status === 200) {
        return response.body;
      }

      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-office-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspaceFeatures'] });
    },
  });
};

// ── Admin Regulatory Watch ──

export const useAdminReferencesApi = (filters?: AdminListReferencesQuery) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-references', filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await client.backOffice.listAdminReferences({
        query: filters ?? {},
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch references');
    },
  });
};

export const useAdminCreateReferenceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateReferenceBody) => {
      const response = await client.backOffice.createAdminReference({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-references'] });
    },
  });
};

export const useAdminUpdateReferenceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateReferenceBody;
    }) => {
      const response = await client.backOffice.updateAdminReference({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-references'] });
    },
  });
};

export const useAdminDeleteReferenceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteAdminReference({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-references'] });
    },
  });
};

export const useAdminUpdatesApi = (filters?: AdminListUpdatesQuery) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-updates', filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await client.backOffice.listAdminUpdates({
        query: filters ?? {},
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch updates');
    },
  });
};

export const useAdminCreateUpdateApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateUpdateBody) => {
      const response = await client.backOffice.createAdminUpdate({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminUpdateUpdateApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateUpdateBody;
    }) => {
      const response = await client.backOffice.updateAdminUpdate({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

export const useAdminDeleteUpdateApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteAdminUpdate({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

export const useAdminDiscardUpdateApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.discardAdminUpdate({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

export const useAdminVersionsApi = (referenceId?: string) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-versions', referenceId],
    enabled: !!referenceId,
    queryFn: async () => {
      const response = await client.backOffice.listAdminVersionsForReference({
        params: { id: referenceId! },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch versions');
    },
  });
};

export const useAdminCreateVersionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateVersionBody) => {
      const response = await client.backOffice.createAdminVersion({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminUpdateVersionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateVersionBody;
    }) => {
      const response = await client.backOffice.updateAdminVersion({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminDeleteVersionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteAdminVersion({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminUploadVersionFileApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      versionId,
      file,
    }: {
      versionId: string;
      file: File;
    }) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(
        `${baseUrl}/admin/regulatory-watch/versions/${versionId}/file`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminDeleteVersionFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (versionId: string) => {
      const response = await client.backOffice.deleteAdminVersionFile({
        params: { id: versionId },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reference-detail'] });
    },
  });
};

export const useAdminUploadUpdateFileApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      updateId,
      file,
    }: {
      updateId: string;
      file: File;
    }) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(
        `${baseUrl}/admin/regulatory-watch/updates/${updateId}/file`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-update-detail'] });
    },
  });
};

export const useAdminDeleteUpdateFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId: string) => {
      const response = await client.backOffice.deleteAdminUpdateFile({
        params: { id: updateId },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-update-detail'] });
    },
  });
};

// ── Admin Reference Detail ──

export const useAdminReferenceDetailApi = (referenceId: string | undefined) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-reference-detail', referenceId],
    enabled: !!referenceId,
    queryFn: async () => {
      const response = await client.backOffice.getAdminReferenceDetail({
        params: { id: referenceId! },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch reference detail');
    },
  });
};

export const useAdminUpdateReferenceDetailApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateAdminReferenceDetailBody;
    }) => {
      const response = await client.backOffice.updateAdminReferenceDetail({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-reference-detail'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-references'] });
      queryClient.invalidateQueries({ queryKey: ['admin-versions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

// ── Admin Update Detail + Redline Generation ──

export const useAdminUpdateDetailApi = (updateId: string | undefined) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-update-detail', updateId],
    enabled: !!updateId,
    queryFn: async () => {
      const response = await client.backOffice.getAdminUpdate({
        params: { id: updateId! },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch update detail');
    },
    refetchInterval: query => {
      const data = query.state.data;
      if (data && data.redline.status === 'generating') return 5000;
      return false;
    },
  });
};

export const useAdminGenerateRedlineApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId: string) => {
      const response = await client.backOffice.generateAdminRedline({
        params: { id: updateId },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: (_data, updateId) => {
      queryClient.invalidateQueries({
        queryKey: ['admin-update-detail', updateId],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

export const useAdminGenerateSummaryApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId: string) => {
      const response = await client.backOffice.generateAdminSummary({
        params: { id: updateId },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: (_data, updateId) => {
      queryClient.invalidateQueries({
        queryKey: ['admin-update-detail', updateId],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

// ── Admin Regulatory Watch: Source Icon Upload ──

export const useAdminUploadSourceIconApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceId,
      file,
    }: {
      sourceId: string;
      file: File;
    }) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(
        `${baseUrl}/admin/regulatory-watch/sources/${sourceId}/icon`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Icon upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sources'] });
    },
  });
};

// ── Admin Regulatory Watch: Sources ──

export const useAdminSourcesApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-sources'],
    queryFn: async () => {
      const response = await client.backOffice.listAdminSources();
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch sources');
    },
  });
};

export const useAdminCreateSourceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateSourceBody) => {
      const response = await client.backOffice.createAdminSource({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sources'] });
    },
  });
};

export const useAdminUpdateSourceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateSourceBody;
    }) => {
      const response = await client.backOffice.updateAdminSource({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sources'] });
    },
  });
};

export const useAdminDeleteSourceApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteAdminSource({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sources'] });
    },
  });
};

// ── Institutions ──

export const useAdminInstitutionsApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-institutions'],
    queryFn: async () => {
      const response = await client.backOffice.listInstitutions();
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch institutions');
    },
  });
};

export const useAdminCreateInstitutionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateInstitutionBody) => {
      const response = await client.backOffice.createInstitution({ body });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to create institution'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-institutions'] });
    },
  });
};

export const useAdminUpdateInstitutionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateInstitutionBody;
    }) => {
      const response = await client.backOffice.updateInstitution({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to update institution'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-institutions'] });
    },
  });
};

export const useAdminDeleteInstitutionApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteInstitution({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to delete institution'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-institutions'] });
    },
  });
};

// ── Source Scraping (interactive discovery) ──

export const useStartSourceScrapingApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useMutation({
    mutationFn: async (body: StartSourceScrapingBody) => {
      const response = await client.backOffice.startSourceScraping({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
  });
};

export const useGetSourceScrapingStatusApi = (
  workflowId: string | null,
  enabled: boolean
) => {
  const { client, isLoaded, isSignedIn } = useAuthenticatedClient(false);

  return useQuery({
    queryKey: ['source-scraping-status', workflowId],
    enabled: !!workflowId && enabled && isLoaded && !!isSignedIn,
    refetchInterval: query => {
      const status = query.state.data?.workflowStatus;
      if (status === 'RUNNING') return 2000;
      return false;
    },
    queryFn: async () => {
      const response = await client.backOffice.getSourceScrapingStatus({
        params: { workflowId: workflowId! },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch scraping status');
    },
  });
};

export const useConfirmSourceScrapingApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: ConfirmSourceScrapingBody) => {
      const response = await client.backOffice.confirmSourceScraping({ body });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sources'] });
      queryClient.invalidateQueries({ queryKey: ['admin-references'] });
      queryClient.invalidateQueries({ queryKey: ['admin-updates'] });
    },
  });
};

export const useCancelSourceScrapingApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await client.backOffice.cancelSourceScraping({
        params: { workflowId },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    },
  });
};

// ── Discarded Source Items ──

export const useDiscardedItemsApi = (source?: string) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-discarded-items', source ?? ''],
    queryFn: async () => {
      const response = await client.backOffice.listDiscardedItems({
        query: source ? { source } : {},
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch discarded items');
    },
    placeholderData: keepPreviousData,
  });
};

// ── Countries ──

export const useAdminCountriesApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-countries'],
    queryFn: async () => {
      const response = await client.backOffice.listCountries();
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch countries');
    },
  });
};

export const useAdminGetCountryApi = (id: string | undefined) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-country', id],
    enabled: !!id,
    queryFn: async () => {
      const response = await client.backOffice.getCountry({
        params: { id: id! },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch country');
    },
  });
};

export const useAdminCreateCountryApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateCountryBody) => {
      const response = await client.backOffice.createCountry({ body });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to create country'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] });
    },
  });
};

export const useAdminUpdateCountryApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateCountryBody;
    }) => {
      const response = await client.backOffice.updateCountry({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to update country'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] });
      queryClient.invalidateQueries({ queryKey: ['admin-country'] });
    },
  });
};

export const useAdminDeleteCountryApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteCountry({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message || 'Failed to delete country'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] });
    },
  });
};

// ── Consultant Organizations ──

export const useAdminConsultantOrgsApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-consultant-orgs'],
    queryFn: async () => {
      const response = await client.backOffice.listConsultantOrgs();
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch consultant organizations');
    },
  });
};

export const useAdminCreateConsultantOrgApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminCreateConsultantOrgBody) => {
      const response = await client.backOffice.createConsultantOrg({ body });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message ||
          'Failed to create consultant organization'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consultant-orgs'] });
    },
  });
};

export const useAdminUpdateConsultantOrgApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AdminUpdateConsultantOrgBody;
    }) => {
      const response = await client.backOffice.updateConsultantOrg({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message ||
          'Failed to update consultant organization'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consultant-orgs'] });
    },
  });
};

export const useAdminDeleteConsultantOrgApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteConsultantOrg({
        params: { id },
        body: undefined as any,
      });
      if (response.status === 200) return response.body;
      throw new Error(
        (response.body as any)?.message ||
          'Failed to delete consultant organization'
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consultant-orgs'] });
    },
  });
};

export const useAdminUploadConsultantOrgLogoApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, file }: { orgId: string; file: File }) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(
        `${baseUrl}/admin/consultant-orgs/${orgId}/logo`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Logo upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consultant-orgs'] });
    },
  });
};

// ── API Clients ──

export const useAdminApiClientsApi = () => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['admin-api-clients'],
    queryFn: async () => {
      const response = await client.backOffice.listApiClients();
      if (response.status === 200) return response.body;
      throw new Error('Failed to fetch API clients');
    },
  });
};

export const useCreateApiClientApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateApiClientBody) => {
      const response = await client.backOffice.createApiClient({ body });
      if (response.status === 201) return response.body;
      throw new Error('Failed to create API client');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-api-clients'] });
    },
  });
};

export const useUpdateApiClientApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateApiClientBody;
    }) => {
      const response = await client.backOffice.updateApiClient({
        params: { id },
        body,
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to update API client');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-api-clients'] });
    },
  });
};

export const useDeleteApiClientApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.backOffice.deleteApiClient({
        params: { id },
      });
      if (response.status === 200) return response.body;
      throw new Error('Failed to delete API client');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-api-clients'] });
    },
  });
};
